import warning from 'warning';

import CanvasHandler from './CanvasHandler';
import { CurvedLink } from '../canvas/fabric';
import { NodeObject } from '../canvas/fabric/Node';
import { PortObject } from '../canvas/fabric/Port';
import { LinkObject } from '../canvas/fabric/Link';

export interface LinkOption {
	/**
	 * Link Type
	 * @type {string}
	 */
	type: string;
	/**
	 * FromNode id of Link
	 * @type {string}
	 */
	fromNodeId?: string;
	/**
	 * FromPort id of Link
	 * @type {string}
	 */
	fromPortId?: string;
	/**
	 * ToNode id of Link
	 * @type {string}
	 */
	toNodeId?: string;
	/**
	 * ToPort id of Link
	 * @type {string}
	 */
	toPortId?: string;
}

/**
 * Link CanvasHandler Class
 * @author salgum1114
 * @class LinkHandler
 */
class LinkHandler {
	private port: PortObject = null;
	canvasHandler: CanvasHandler;

	constructor(canvasHandler: CanvasHandler) {
		this.canvasHandler = canvasHandler;
	}

	/**
	 * On source port click, start link
	 * @param {PortObject} port
	 */
	init = (port: PortObject) => {
		if (this.isDrawing()) {
			return;
		}
		if (this.isConnected(port)) {
			return;
		}
		this.port = port;
		this.port.set({
			fill: port.selectFill,
		});
		this.canvasHandler.interactionHandler.linking();
		const { left, top, nodeId, id } = port;
		const fromPort = { left, top, id };
		const toPort = { left, top };
		const fromNode = this.canvasHandler.objectMap[nodeId];
		this.canvasHandler.activeLine = new CurvedLink(fromNode, fromPort, null, toPort, {
			strokeWidth: 2,
			fill: '#999999',
			stroke: '#999999',
			class: 'line',
			originX: 'center',
			originY: 'center',
			selectable: false,
			hasBorders: false,
			hasControls: false,
			evented: false,
		});
		this.canvasHandler.canvas.add(this.canvasHandler.activeLine);
	};

	/**
	 * End drawing link.
	 */
	finish = (link?: LinkObject) => {
		if (!link) {
			this.port.set({
				fill: this.port.originFill,
			});
		}
		this.canvasHandler.interactionHandler.selection();
		this.canvasHandler.canvas.remove(this.canvasHandler.activeLine);
		this.canvasHandler.activeLine = null;
		this.canvasHandler.canvas.renderAll();
	};

	/**
	 * On dest port click, finish link
	 * @param {PortObject} port
	 */
	generate = (port: PortObject) => {
		if (!port) {
			warning(!port, 'Does not exist target port.');
			return;
		}
		if (this.isDuplicate(port)) {
			return;
		}
		if (this.isSameNode(port)) {
			return;
		}
		const link = {
			type: 'curvedLink',
			fromNodeId: this.canvasHandler.activeLine.fromNode.id,
			fromPortId: this.canvasHandler.activeLine.fromPort.id,
			toNodeId: port.nodeId,
			toPortId: port.id,
		};
		const createdLink = this.create(link);
		this.finish(createdLink);
		// TODO...
		// Save transactions unconditionally
		if (!this.canvasHandler.transactionHandler.active) {
			this.canvasHandler.transactionHandler.save('add');
		}
	};

	/**
	 * Add link in Canvas
	 * @param {LinkOption} link
	 * @param {boolean} [loaded=false]
	 * @returns
	 */
	create = (link: LinkOption, loaded = false) => {
		const fromNode = this.canvasHandler.objectMap[link.fromNodeId] as NodeObject;
		const fromPort = fromNode.fromPort.filter(port => port.id === link.fromPortId || !port.id)[0];
		const toNode = this.canvasHandler.objectMap[link.toNodeId] as NodeObject;
		const { toPort } = toNode;
		const createdObj = this.canvasHandler.fabricObjects[link.type].create(fromNode, fromPort, toNode, toPort, {
			...link,
		}) as LinkObject;
		this.canvasHandler.canvas.add(createdObj);
		this.canvasHandler.objects = this.canvasHandler.getObjects();
		const { editable } = this.canvasHandler;
		if (this.canvasHandler.onAdd && editable && !loaded) {
			this.canvasHandler.onAdd(createdObj);
		}
		this.canvasHandler.canvas.renderAll();
		createdObj.setPort(fromNode, fromPort, toNode, toPort);
		this.canvasHandler.portHandler.setCoords(fromNode);
		this.canvasHandler.portHandler.setCoords(toNode);
		this.canvasHandler.canvas.requestRenderAll();
		return createdObj;
	};

	/**
	 * Set coordinate of link
	 * @param {number} x1
	 * @param {number} y1
	 * @param {number} x2
	 * @param {number} y2
	 * @param {LinkObject} link
	 */
	setCoords = (x1: number, y1: number, x2: number, y2: number, link: LinkObject) => {
		link.set({
			x1,
			y1,
			x2,
			y2,
		});
		link.setCoords();
	};

	/**
	 * When the link is deleted, linked FromNode delete
	 * @param {LinkObject} link
	 */
	removeFrom = (link: LinkObject) => {
		if (link.fromPort) {
			let index = -1;
			if (link.fromPort.links.length) {
				link.fromPort.links.some((portLink: any, i: number) => {
					if (link.id === portLink.id) {
						index = i;
						return true;
					}
					return false;
				});
				if (index > -1) {
					link.fromPort.links.splice(index, 1);
				}
			}
			link.setPortEnabled(link.fromNode, link.fromPort, true);
		}
	};

	/**
	 * When the link is deleted, linked ToNode delete
	 * @param {LinkObject} link
	 */
	removeTo = (link: LinkObject) => {
		if (link.toNode.toPort.links.length) {
			let index = -1;
			link.toNode.toPort.links.some((portLink: any, i: number) => {
				if (link.id === portLink.id) {
					index = i;
					return true;
				}
				return false;
			});
			if (index > -1) {
				link.toNode.toPort.links.splice(index, 1);
			}
			link.setPortEnabled(link.toNode, link.toNode.toPort, true);
		}
	};

	/**
	 * When the link is deleted, linked node delete
	 * @param {LinkObject} link
	 */
	removeAll = (link: LinkObject) => {
		this.removeFrom(link);
		this.removeTo(link);
	};

	/**
	 * Remove link in canvas
	 * @param {LinkObject} link
	 * @param {string} [type]
	 */
	remove = (link: LinkObject, type?: string) => {
		if (type === 'from') {
			this.removeFrom(link);
		} else if (type === 'to') {
			this.removeTo(link);
		} else {
			this.removeAll(link);
		}
		this.canvasHandler.canvas.remove(link);
		this.canvasHandler.objects = this.canvasHandler.getObjects();
	};

	/**
	 * Check if there is a port connected
	 * @param {PortObject} port
	 * @returns
	 */
	isConnected = (port: PortObject) => {
		warning(port.enabled, 'A connected node already exists.');
		return !port.enabled;
	};

	/**
	 * Check if select same node
	 * @param {PortObject} port
	 * @returns
	 */
	isSameNode = (port: PortObject) => {
		const validate = port.nodeId === this.canvasHandler.activeLine.fromNode.id;
		warning(!validate, 'Cannot select the same node.');
		return validate;
	};

	/**
	 * Check if select same node
	 * @param {PortObject} port
	 * @returns
	 */
	isDuplicate = (port: PortObject) => {
		const validate = port.links.some(link => link.fromNode.id === this.canvasHandler.activeLine.fromNode.id);
		warning(!validate, 'Duplicate connections cannot be made.');
		return validate;
	};

	/**
	 * Check if draw the link
	 * @returns
	 */
	isDrawing = () => {
		const validate = this.canvasHandler.interactionMode === 'link' && this.canvasHandler.activeLine;
		warning(!validate, 'Already drawing links.');
		return validate;
	};
}

export default LinkHandler;
